import React from 'react';
import {Box, CircularProgress, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {ArcElement, Chart as ChartJS, Legend, Tooltip} from 'chart.js';
import {Doughnut} from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import {Colors} from '@constants/colors.constants';
import dayjs from '@utils/dayjs.utils';
import {useObservable} from '@ngneat/react-rxjs';
import {statisticsQuery} from '@store/statistics';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface ChargedLineChartProps {
  type: 'SUPPLIER' | 'CLIENT';
}

const ChargedLinesChart = (props: ChargedLineChartProps) => {
  const { type } = props;
  const {t} = useTranslation();

  const [{donutChart, loading}] = useObservable(statisticsQuery.donutChart$);

  const getIntegratedCount = () => {
    return type === 'SUPPLIER' ? donutChart?.supplierLinesIntegrated : donutChart?.clientLinesIntegrated;
  };

  const getEligibleCount = () => {
    return type === 'SUPPLIER' ? donutChart?.supplierLinesEligible : donutChart?.clientLinesEligible;
  };

  const getPercentageData = () => {
    const percentIntegrated = (getIntegratedCount() || 0) / (getEligibleCount() || 0) * 100;
    const percentNotIntegrated = 100 - percentIntegrated;

    return [percentIntegrated.toFixed(2), percentNotIntegrated.toFixed(2)];
  };

  return (
    <Stack
      flex={1}
      height="360px"
      bgcolor={Colors.white}
      borderRadius="4px"
      px="3px"
      py="20px"
      justifyContent="space-between"
    >
      <Typography fontSize={18} fontWeight="700" ml="17px">
        {t(`dashboard.statistics.finance.chargedLines.title.${type}`)}
      </Typography>
      <Typography fontSize={11} ml="17px" mb="20px">
        {dayjs().subtract(1, 'month').format('MMMM YYYY')}
      </Typography>
      {loading && <CircularProgress size={30}/>}
      {!loading && <Stack
          height="220px"
          width="220px"
          alignSelf="center"
          position="relative"
          alignItems="center"
          justifyContent="center"
      >
          <Doughnut
              options={{
                plugins: {
                  datalabels: {
                    display: true,
                    color: function (ctx) {
                      return ctx.dataIndex === 0 ? Colors.white : Colors.text;
                    },
                    formatter: function (value) {
                      return `${value}%`;
                    },
                    textStrokeColor: Colors.white,
                    textStrokeWidth: function (ctx) {
                      return ctx.dataIndex === 0 ? 0 : 2;
                    },
                    font: {
                      family: 'CircularStd, sans-serif',
                      size: 14,
                      weight: 'bold',
                    },
                  },
                  legend: {display: false},
                  title: {display: false},
                  tooltip: {enabled: false},
                },
                cutout: 75,
              }}
              plugins={[ChartDataLabels]}
              data={{
                labels: dayjs.weekdaysShort(),
                datasets: [
                  {
                    data: [...getPercentageData()],
                    backgroundColor: [Colors.blue, Colors.yellow],
                    borderWidth: 0,
                    weight: 5,
                  },
                ],
              }}
          />
          <Stack direction="row" position="absolute" alignItems="center" mt="10px">
              <Typography fontSize={30} fontWeight="700" mt="-20px">
                {getIntegratedCount()}
              </Typography>
              <Typography fontSize={18} fontWeight="700" color={Colors.textWithOpacity}>
                  /{getEligibleCount()}
              </Typography>
          </Stack>
      </Stack>}
      <Stack direction="row" alignItems="center" alignSelf="center" spacing={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" bgcolor={Colors.blue} borderRadius="3px"/>
          <Typography fontSize={12} fontWeight="400">
            {t(`dashboard.statistics.finance.chargedLines.totalBills.${type}`)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" bgcolor={Colors.yellow} borderRadius="3px"/>
          <Typography fontSize={12} fontWeight="400">
            {t(`dashboard.statistics.finance.chargedLines.totalChargedLines.${type}`)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ChargedLinesChart;
